/**
 * Hook para chamada de vídeo 1:1 WebRTC.
 * Sinalização via Socket.IO: call:offer, call:answer, call:ice, call:hangup.
 */
import { useState, useCallback, useEffect, useRef } from 'react';

async function getLocalStream() {
  try {
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!md?.getUserMedia) {
      const legacy = typeof navigator !== 'undefined' && (navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia);
      if (legacy) {
        return await new Promise((resolve, reject) => {
          legacy.call(navigator, { video: true, audio: true }, resolve, reject);
        });
      }
      console.warn('[useVideoCall] getUserMedia não disponível. Use HTTPS e permita acesso à câmera.');
      return null;
    }
    return await md.getUserMedia({ video: true, audio: true });
  } catch (e) {
    console.warn('[useVideoCall] getUserMedia failed:', e);
    return null;
  }
}

const defaultConfig = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
};

export function useVideoCall(socket, userId) {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle | calling | incoming | connected
  const [remoteUser, setRemoteUser] = useState(null);
  const [incomingFrom, setIncomingFrom] = useState(null); // { id, nome } do caller
  const pcRef = useRef(null);
  const pendingRemoteUserRef = useRef(null);
  const remoteUserIdRef = useRef(null);
  const incomingFromIdRef = useRef(null);
  remoteUserIdRef.current = remoteUser?.id ?? null;
  incomingFromIdRef.current = incomingFrom?.id ?? null;

  const cleanup = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((t) => t.stop());
      setLocalStream(null);
    }
    setRemoteStream(null);
    setRemoteUser(null);
  }, [localStream]);

  const hangup = useCallback(() => {
    const targetId = remoteUserIdRef.current || remoteUser?.id || (callState === 'incoming' ? incomingFromIdRef.current || incomingFrom?.id : null);
    if (socket && targetId) {
      socket.emit('call:hangup', { targetUserId: targetId });
    }
    remoteUserIdRef.current = null;
    incomingFromIdRef.current = null;
    cleanup();
    setCallState('idle');
    setIncomingFrom(null);
  }, [socket, remoteUser, incomingFrom, callState, cleanup]);

  // Outgoing call
  const startCall = useCallback(
    async (contact) => {
      if (!socket || !userId || !contact?.id) return false;
      const stream = await getLocalStream();
      if (!stream) return false;

      const pc = new RTCPeerConnection(defaultConfig);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pcRef.current = pc;

      pc.ontrack = (e) => {
        if (e.streams?.[0]) setRemoteStream(e.streams[0]);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && socket && contact?.id) {
          socket.emit('call:ice', { targetUserId: contact.id, candidate: e.candidate });
        }
      };

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('call:offer', {
          targetUserId: contact.id,
          offer: { type: offer.type, sdp: offer.sdp }
        });
      } catch (err) {
        console.error('[useVideoCall] createOffer failed:', err);
        cleanup();
        return false;
      }

      setLocalStream(stream);
      setRemoteUser(contact);
      setCallState('calling');
      return true;
    },
    [socket, userId, cleanup]
  );

  // Accept incoming call
  const acceptCall = useCallback(
    async () => {
      if (!socket || !userId || !incomingFrom?.id) return false;
      const stream = await getLocalStream();
      if (!stream) return false;

      const pc = new RTCPeerConnection(defaultConfig);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pcRef.current = pc;

      pc.ontrack = (e) => {
        if (e.streams?.[0]) setRemoteStream(e.streams[0]);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && socket && incomingFrom?.id) {
          socket.emit('call:ice', { targetUserId: incomingFrom.id, candidate: e.candidate });
        }
      };

      const offer = pendingRemoteUserRef.current?.offer;
      if (!offer) {
        cleanup();
        setIncomingFrom(null);
        setCallState('idle');
        return false;
      }

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('call:answer', {
          targetUserId: incomingFrom.id,
          answer: { type: answer.type, sdp: answer.sdp }
        });
      } catch (err) {
        console.error('[useVideoCall] createAnswer failed:', err);
        cleanup();
        setIncomingFrom(null);
        setCallState('idle');
        return false;
      }

      remoteUserIdRef.current = incomingFrom.id;
      incomingFromIdRef.current = null;
      setLocalStream(stream);
      setRemoteUser({ id: incomingFrom.id, nome: incomingFrom.nome });
      setIncomingFrom(null);
      pendingRemoteUserRef.current = null;
      setCallState('connected');
      return true;
    },
    [socket, userId, incomingFrom, cleanup]
  );

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (socket && incomingFrom?.id) {
      socket.emit('call:hangup', { targetUserId: incomingFrom.id });
    }
    setIncomingFrom(null);
    setCallState('idle');
    pendingRemoteUserRef.current = null;
  }, [socket, incomingFrom]);

  // Socket listeners
  useEffect(() => {
    if (!socket || !userId) return;

    const onOffer = (data) => {
      if (data.targetUserId !== userId) return;
      // Avoid overwriting if already in active call
      if (remoteUserIdRef.current || (incomingFromIdRef.current && pendingRemoteUserRef.current)) return;
      incomingFromIdRef.current = data.fromUserId;
      setIncomingFrom({
        id: data.fromUserId,
        nome: data.fromUserName || data.fromUserEmail || 'Contato'
      });
      pendingRemoteUserRef.current = { offer: data.offer };
      setCallState('incoming');
    };

    const onAnswer = (data) => {
      if (data.targetUserId !== userId) return;
      if (callState !== 'calling' || !pcRef.current) return;
      const answer = data.answer;
      if (!answer) return;
      pcRef.current
        .setRemoteDescription(new RTCSessionDescription(answer))
        .then(() => setCallState('connected'))
        .catch((err) => console.error('[useVideoCall] setRemoteDescription failed:', err));
    };

    const onIce = (data) => {
      if (data.targetUserId !== userId) return;
      const candidate = data.candidate;
      if (!candidate || !pcRef.current) return;
      pcRef.current.addIceCandidate(new RTCIceCandidate(candidate)).catch((e) => console.warn('[useVideoCall] addIceCandidate:', e));
    };

    const onHangup = (data) => {
      const otherId = data.fromUserId;
      const isFromRemote = remoteUserIdRef.current === otherId || incomingFromIdRef.current === otherId;
      if (!isFromRemote) return;
      remoteUserIdRef.current = null;
      incomingFromIdRef.current = null;
      cleanup();
      setCallState('idle');
      setIncomingFrom(null);
      setRemoteUser(null);
    };

    socket.on('call:offer', onOffer);
    socket.on('call:answer', onAnswer);
    socket.on('call:ice', onIce);
    socket.on('call:hangup', onHangup);

    return () => {
      socket.off('call:offer', onOffer);
      socket.off('call:answer', onAnswer);
      socket.off('call:ice', onIce);
      socket.off('call:hangup', onHangup);
    };
  }, [socket, userId, cleanup]);

  return {
    localStream,
    remoteStream,
    callState,
    remoteUser,
    incomingFrom,
    startCall,
    acceptCall,
    rejectCall,
    hangup,
    isOpen: callState !== 'idle'
  };
}
